import { Fragment, useRef } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Pagination } from "@/components/Pagination";
import { Button, Card } from "@/components/ui";
import { CompanyRow } from "./CompanyMatches";
import { ContactMatches } from "./ContactMatches";
import { blockerFor, isAlreadyACompany } from "./model";
import type {
  BranchMatch,
  CompanyPage,
  Keyword,
  SimilarityQuery,
  Similarities,
} from "./queries";
import type { Capture } from "./rows";

/**
 * One list on the screen, in whichever of its three states it is in.
 *
 * Not searched yet, searched and empty, and searched with rows are three
 * different things to tell reception — "type the company", "nobody by that
 * name" and "here they are" — and a blank panel says none of them.
 */
function Block({
  title,
  aside,
  asked,
  askFor,
  empty,
  hasMore,
  children,
}: {
  title?: ReactNode;
  /** Beside the title: what this block searched on. */
  aside?: ReactNode;
  /** Whether the field this block reads has enough in it to search. */
  asked: boolean;
  /** What to type, while it has not. */
  askFor: ReactNode;
  empty: boolean;
  hasMore: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section>
      {title ? (
        <h5 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-sm font-semibold">
          {title}
          {aside ? <span className="font-normal text-mute-600">{aside}</span> : null}
        </h5>
      ) : null}
      {!asked ? (
        <p className="text-sm text-mute-600">{askFor}</p>
      ) : empty ? (
        <p className="text-sm text-mute-600">{t("reception.noMatches")}</p>
      ) : (
        children
      )}
      {asked && hasMore ? (
        <p className="mt-2 text-xs text-brand-700">{t("reception.hasMore")}</p>
      ) : null}
    </section>
  );
}

/** Branches whose own line or address matched: the company, and why it is here. */
function BranchRows({
  branches,
  capture,
  reason,
}: {
  branches: readonly BranchMatch[];
  capture: Capture;
  reason: (branch: BranchMatch) => string;
}) {
  if (branches.length === 0) return null;
  return (
    <ul className="mt-2 space-y-2">
      {branches.map((branch) => (
        <CompanyRow
          key={branch.id}
          company={branch.company}
          capture={capture}
          note={reason(branch)}
        />
      ))}
    </ul>
  );
}

/** "mcm (6), technologies (50)": each word block two used, and its haul. */
function KeywordCounts({ keywords }: { keywords: readonly Keyword[] }) {
  const { t } = useTranslation();
  return keywords.map((keyword, index) => (
    <Fragment key={keyword.word}>
      {index > 0 ? ", " : null}
      <span className="font-semibold text-brand-700">
        {t("reception.keywordCount", {
          word: keyword.word,
          total: keyword.count.toLocaleString("es-MX"),
        })}
      </span>
    </Fragment>
  ));
}

const NOTHING = { results: [], has_more: false } as const;
const NO_PAGE: CompanyPage = { results: [], count: 0, page: 1, page_size: 20 };

/**
 * The four sections of the capture window: *Similitudes por* empresa, contacto,
 * teléfono and correo.
 *
 * The company section is two lists and stays two. A row under *Nombre
 * completo* is very likely the caller; a row under *Palabras clave* shares a
 * word with them. Merged, reception would have to work out which is which on
 * every call. The second is the one that runs long — one common word is in a
 * hundred names — so it is read a page at a time.
 *
 * *Crear empresa* comes last, under all four: the company is new only once
 * every list has been read, and the button is where the reading ends.
 */
export function Similar({
  found,
  query,
  capture,
  onKeywordsPage,
  paging,
  onCreateCompany,
}: {
  found: Similarities | undefined;
  /** What `found` was searched on, so each block knows whether it was asked. */
  query: SimilarityQuery;
  capture: Capture;
  /** Turn *Palabras clave* to another page. */
  onKeywordsPage: (page: number) => void;
  /** A search is on its way, a new page among what it may bring. */
  paging: boolean;
  onCreateCompany: () => void;
}) {
  const { t } = useTranslation();
  const keywordsTop = useRef<HTMLDivElement>(null);
  const byName = found?.companies_by_name ?? NOTHING;
  const byKeywords = found?.companies_by_keywords ?? NO_PAGE;
  const keywords = found?.keywords ?? [];
  const contactsByName = found?.contacts_by_name ?? NOTHING;
  const contactsByPhone = found?.contacts_by_phone ?? NOTHING;
  const contactsByEmail = found?.contacts_by_email ?? NOTHING;
  const branchesByPhone = found?.branches_by_phone ?? NOTHING;
  const branchesByEmail = found?.branches_by_email ?? NOTHING;

  const alreadyExists = isAlreadyACompany(capture.draft.companyName, byName.results);
  const blocker = blockerFor(capture.draft, capture.agent?.id ?? null, null);

  function turnKeywordsPage(page: number) {
    // The buttons are under the list; the new page starts at its top.
    keywordsTop.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    onKeywordsPage(page);
  }

  return (
    <div className="space-y-4">
      <Card title={t("reception.sections.company")}>
        <div className="space-y-5 p-4">
          <Block
            title={t("reception.byFullName")}
            asked={Boolean(query.company_name)}
            askFor={t("reception.askFor.company")}
            empty={byName.results.length === 0}
            hasMore={byName.has_more}
          >
            <ul className="space-y-2">
              {byName.results.map((company) => (
                <CompanyRow key={company.id} company={company} capture={capture} />
              ))}
            </ul>
          </Block>

          {/* Clear of the sticky *Asignar a* bar when a new page scrolls here. */}
          <div ref={keywordsTop} className="scroll-mt-40 sm:scroll-mt-32">
            <Block
              title={
                <>
                  {t("reception.byKeywords")}
                  {keywords.length > 0 ? ":" : null}
                </>
              }
              aside={
                keywords.length > 0 ? <KeywordCounts keywords={keywords} /> : undefined
              }
              asked={Boolean(query.company_name)}
              askFor={t("reception.askFor.company")}
              empty={byKeywords.count === 0}
              hasMore={false}
            >
              <ul className="space-y-2">
                {byKeywords.results.map((company) => (
                  <CompanyRow key={company.id} company={company} capture={capture} />
                ))}
              </ul>
              {byKeywords.count > byKeywords.page_size ? (
                <div className="mt-3">
                  <Pagination
                    page={byKeywords.page}
                    count={byKeywords.count}
                    shown={byKeywords.results.length}
                    pageSize={byKeywords.page_size}
                    isFetching={paging}
                    onChange={turnKeywordsPage}
                  />
                </div>
              ) : null}
            </Block>
          </div>
        </div>
      </Card>

      <Card title={t("reception.sections.contact")}>
        <div className="p-4">
          <Block
            asked={Boolean(query.contact_name)}
            askFor={t("reception.askFor.contact")}
            empty={contactsByName.results.length === 0}
            hasMore={contactsByName.has_more}
          >
            <ContactMatches contacts={contactsByName.results} capture={capture} />
          </Block>
        </div>
      </Card>

      <Card title={t("reception.sections.phone")}>
        <div className="p-4">
          <Block
            asked={Boolean(query.phone)}
            askFor={t("reception.askFor.phone")}
            empty={
              contactsByPhone.results.length === 0 &&
              branchesByPhone.results.length === 0
            }
            hasMore={contactsByPhone.has_more || branchesByPhone.has_more}
          >
            <ContactMatches contacts={contactsByPhone.results} capture={capture} />
            <BranchRows
              branches={branchesByPhone.results}
              capture={capture}
              reason={(branch) =>
                t("reception.branchPhone", {
                  branch: branch.name,
                  phone: branch.sales_phone,
                })
              }
            />
          </Block>
        </div>
      </Card>

      <Card title={t("reception.sections.email")}>
        <div className="p-4">
          <Block
            asked={Boolean(query.email)}
            askFor={t("reception.askFor.email")}
            empty={
              contactsByEmail.results.length === 0 &&
              branchesByEmail.results.length === 0
            }
            hasMore={contactsByEmail.has_more || branchesByEmail.has_more}
          >
            <ContactMatches contacts={contactsByEmail.results} capture={capture} />
            <BranchRows
              branches={branchesByEmail.results}
              capture={capture}
              reason={(branch) => t("reception.branchEmail", { branch: branch.name })}
            />
          </Block>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-2 border-rule bg-ground p-4">
        <p className="mb-0 text-sm text-mute-600">
          {alreadyExists ? t("reception.alreadyExists") : t("reception.notListed")}
        </p>
        <Button
          variant="primary"
          disabled={blocker !== null || alreadyExists || capture.busy}
          title={blocker ? t(`reception.blocked.${blocker}`) : undefined}
          onClick={onCreateCompany}
        >
          {t("reception.createCompany")}
        </Button>
      </div>
    </div>
  );
}
